import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import type { CreateCustomStructureInput } from "@/hooks/use-custom-sections-structures";

// Common body regions for dropdown
const BODY_REGIONS = [
  "HEAD_BRAIN",
  "HEAD_ORBITS",
  "HEAD_SINUSES",
  "NECK_SOFT_TISSUE",
  "NECK_THYROID",
  "SPINE_CERVICAL",
  "SPINE_THORACIC",
  "SPINE_LUMBAR",
  "CHEST_LUNGS",
  "CHEST_MEDIASTINUM",
  "CARDIAC_CHAMBERS",
  "ABDOMEN_LIVER",
  "ABDOMEN_KIDNEYS",
  "ABDOMEN_GI_TRACT",
  "PELVIS_GENERAL",
  "PELVIS_PROSTATE",
  "PELVIS_UTERUS",
  "MSK_SHOULDER",
  "MSK_KNEE",
  "MSK_SPINE",
] as const;

const formSchema = z.object({
  display_name: z
    .string()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be at most 100 characters"),
  structure_key: z
    .string()
    .min(2, "Key must be at least 2 characters")
    .max(50, "Key must be at most 50 characters")
    .regex(
      /^[a-z][a-z0-9_]*$/,
      "Key must be lower_snake_case (e.g., my_custom_structure)"
    ),
  body_region: z.string().optional(),
  section_token: z.string().optional(),
  scope: z.enum(["template", "user"]),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateCustomStructureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: CreateCustomStructureInput) => Promise<unknown>;
  sectionToken?: string;
  sectionOptions?: Array<{ token: string; title: string }>;
}

export function CreateCustomStructureDialog({
  open,
  onOpenChange,
  onCreate,
  sectionToken,
  sectionOptions = [],
}: CreateCustomStructureDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      display_name: "",
      structure_key: "",
      body_region: "",
      section_token: sectionToken || "",
      scope: "user",
    },
  });

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const result = await onCreate({
        structure_key: values.structure_key,
        display_name: values.display_name,
        body_region: values.body_region || undefined,
        section_token: values.section_token || undefined,
        scope: values.scope,
      });

      if (result) {
        form.reset();
        onOpenChange(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-generate key from display name
  const handleNameChange = (name: string) => {
    form.setValue("display_name", name);
    // Only auto-generate if key is empty or was auto-generated
    const currentKey = form.getValues("structure_key");
    if (!currentKey || currentKey === generateKey(form.getValues("display_name"))) {
      form.setValue("structure_key", generateKey(name));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Custom Structure</DialogTitle>
          <DialogDescription>
            Create a new anatomical structure that you can add to your sections.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Accessory spleen"
                      {...field}
                      onChange={(e) => handleNameChange(e.target.value)}
                    />
                  </FormControl>
                  <FormDescription>
                    The name shown in the structure list
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="structure_key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Structure Key</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., accessory_spleen"
                      className="font-mono"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    Unique identifier in lower_snake_case
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="body_region"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Body Region (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a body region" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="">None</SelectItem>
                      {BODY_REGIONS.map((region) => (
                        <SelectItem key={region} value={region}>
                          {region.replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Categorize this structure by body region
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {sectionOptions.length > 0 && (
              <FormField
                control={form.control}
                name="section_token"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Section (Optional)</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a section" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="">None</SelectItem>
                        {sectionOptions.map((section) => (
                          <SelectItem key={section.token} value={section.token}>
                            {section.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Which section this structure belongs to by default
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="scope"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Scope</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select scope" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="user">
                        All my templates
                      </SelectItem>
                      <SelectItem value="template">
                        This template only
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Where this custom structure can be used
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Structure
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// Helper to generate key from display name
function generateKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "")
    .substring(0, 50);
}
